import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { PopoutTransitionTimeout } from 'modules/ui/popout/Popout';
import { PopoutTheme } from 'modules/ui/popout/PopoutTheme';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { PopoutSelectors } from '../redux';
import {
  setPopoutContainer,
  setPopoutVisible,
  setTransitioningState,
} from '../redux/slice';

export interface OpenContainerInPopoutArgs {
  newContainer: React.ReactNode;
  urlKey?: string;
  theme?: PopoutTheme;
}

export const useSidePopout = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const container = useAppSelector(PopoutSelectors.selectPopoutContainer);
  const popoutVisible = useAppSelector(PopoutSelectors.selectPopoutVisible);
  const transitioning = useAppSelector(
    PopoutSelectors.selectTransitioningState,
  );
  const popoutTheme = useAppSelector(PopoutSelectors.selectPopoutTheme);

  const changePopoutContainer = useCallback(
    (args: OpenContainerInPopoutArgs) => {
      const { newContainer, urlKey, theme } = args;
      dispatch(setTransitioningState(true));

      setTimeout(() => {
        dispatch(setPopoutContainer({ newContainer, theme }));
        urlKey && history.push(`${window.location.pathname}#${urlKey}`);
        dispatch(setTransitioningState(false));
      }, PopoutTransitionTimeout);
    },
    [dispatch, history],
  );

  const getTransitioningState = useCallback(
    () => transitioning,
    [transitioning],
  );

  const getPopoutContainer = useCallback(() => container, [container]);
  const getPopoutTheme = useCallback(() => popoutTheme, [popoutTheme]);

  const closePopout = useCallback(
    () => dispatch(setPopoutVisible(false)),
    [dispatch],
  );

  const isPopoutOpen = useCallback(() => popoutVisible, [popoutVisible]);

  const openContainerInPopout = (args: OpenContainerInPopoutArgs) => {
    changePopoutContainer(args);
    dispatch(setPopoutVisible(true));
  };

  return {
    changePopoutContainer,
    closePopout,
    openContainerInPopout,
    popoutContainer: getPopoutContainer(),
    popoutVisible: isPopoutOpen(),
    popoutTheme: getPopoutTheme(),
    transitioning: getTransitioningState(),
  };
};
